import { useMemo, useState } from 'react';
import { useAdminI18n } from '../../../lib/i18n/use-admin-i18n';
import { useLocation, useNavigate } from 'react-router-dom';
import type { PaginationState, SortingState } from '@tanstack/react-table';
import toast from 'react-hot-toast';
import type { WorkflowNode, WorkflowScene, WorkflowTimelineItem } from '@cms/models';
import { DynamicTable } from '../../../components/custom/table/DynamicTable';
import type {
  DynamicTableColumnConfig,
  DynamicTableControllers,
} from '../../../components/custom/table/dynamic-table-types';
import { SearchFilter } from '../../../components/custom/filters';
import { useAuthStore } from '../../../stores/authStore';
import { useDeleteProject, useProjects } from '../../../lib/api/projects';
import { withAreaPrefix } from '../../../lib/area-routes';
import {
  AdminTableRowActions,
  PagePrimaryActionLink,
} from '../../../components/common/AdminTableRowActions';
import type { ProjectRow } from './project-types';

interface ProjectsTableProps {
  rows: ProjectRow[];
  controllers?: DynamicTableControllers;
  rowOffset: number;
  canUpdate: boolean;
  canDelete: boolean;
  onView: (project: ProjectRow) => void;
  onEdit: (project: ProjectRow) => void;
  onDelete: (project: ProjectRow) => void;
  isLoading: boolean;
}

function formatDate(value: unknown): string {
  if (!value) return '-';
  const date = new Date(String(value));
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString();
}

function getNodesCount(row: ProjectRow): number {
  const fromApi = row.scene_count ?? row.sceneCount;
  if (typeof fromApi === 'number' && Number.isFinite(fromApi) && fromApi >= 0) {
    return fromApi;
  }
  const nodes = (row.timeline_nodes ?? row.timelineNodes ?? []) as WorkflowTimelineItem[];
  if (!Array.isArray(nodes) || nodes.length === 0) return 0;

  if (nodes.some((item): item is WorkflowScene => Boolean(item && typeof item === 'object' && 'text2image' in item && 'image2video' in item))) {
    return nodes.filter(
      (item): item is WorkflowScene => Boolean(item && typeof item === 'object' && 'text2image' in item && 'image2video' in item),
    ).length;
  }

  const sceneIds = new Set<number>();
  for (const node of nodes as Array<WorkflowNode & { scene_id?: unknown }>) {
    if (typeof node.scene_id === 'number' && Number.isInteger(node.scene_id) && node.scene_id >= 0) {
      sceneIds.add(node.scene_id);
    }
  }
  if (sceneIds.size > 0) {
    return sceneIds.size;
  }

  // Backward compatibility for old payloads without scene_id:
  // each scene is stored as a TEXT_TO_IMAGE + IMAGE_TO_VIDEO pair.
  return Math.ceil(nodes.length / 2);
}

function getTenantDisplayName(row: ProjectRow): string {
  const name = row.tenantName ?? row.tenant_name;
  return name && name.trim() ? name : '—';
}

function ProjectsTable(props: Readonly<ProjectsTableProps>) {
  const { rows, controllers, rowOffset, canUpdate, canDelete, onView, onEdit, onDelete, isLoading } = props;
  const { col, act, page, tableLoading, statusOptions, projectLifecycleOptions, language } =
    useAdminI18n();

  const columns: DynamicTableColumnConfig<ProjectRow>[] = useMemo(
    () => [
    {
      id: 'no',
      header: col('no'),
      columnType: 'number',
      cell: ({ index }) => rowOffset + index + 1,
      widthMode: 'fit',
      size: 64,
      meta: { className: 'w-16 text-muted-foreground' },
    },
    {
      id: 'name',
      accessorKey: 'name',
      header: col('name'),
      sortable: true,
      columnType: 'text',
    },
    {
      id: 'tenantName',
      header: col('tenant'),
      sortable: true,
      columnType: 'text',
      cell: ({ row }) => getTenantDisplayName(row),
      minSize: 160,
      meta: { className: 'min-w-[140px]' },
    },
    {
      id: 'status',
      accessorKey: 'status',
      header: col('status'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: statusOptions,
      widthMode: 'fit',
      size: 96,
      meta: { className: 'min-w-[88px]' },
    },
    {
      id: 'lifecycleStatus',
      accessorFn: (row: ProjectRow) =>
        row.lifecycleStatus ??
        (row as ProjectRow & { lifecycle_status?: string }).lifecycle_status,
      header: col('lifecycle'),
      sortable: true,
      columnType: 'badge',
      displayLabelOptions: projectLifecycleOptions,
      widthMode: 'fit',
      size: 110,
      meta: { className: 'min-w-[100px]' },
    },
    {
      id: 'nodeCount',
      header: col('scenes'),
      sortable: true,
      columnType: 'number',
      cell: ({ row }) => getNodesCount(row),
      widthMode: 'fit',
      size: 100,
      meta: { className: 'min-w-[100px]' },
    },
    {
      id: 'createdAt',
      header: col('created_at'),
      sortable: true,
      columnType: 'text',
      cell: ({ row }) => formatDate(row.created_at ?? row.createdAt),
      minSize: 180,
    },
    {
      id: 'actions',
      header: col('actions'),
      columnType: 'actions',
      cell: ({ row }) => (
        <AdminTableRowActions
          act={act}
          onView={() => onView(row)}
          onEdit={canUpdate ? () => onEdit(row) : undefined}
          onDelete={canDelete ? () => onDelete(row) : undefined}
        />
      ),
      widthMode: 'fit',
      size: 180,
      minSize: 160,
      meta: { className: 'min-w-[160px] text-right' },
    },
    ],
    [
      act,
      canDelete,
      canUpdate,
      col,
      language,
      onDelete,
      onEdit,
      onView,
      projectLifecycleOptions,
      rowOffset,
      statusOptions,
    ],
  );

  return (
    <DynamicTable<ProjectRow>
      features={{
        columns: true,
        rows: true,
        sorting: true,
        pagination: true,
        globalFilter: false,
        stripedRows: true,
        fontSize: 'xs',
        compactPadding: true,
      }}
      columns={columns}
      rows={{
        data: rows,
        getRowId: row => String(row.id),
      }}
      controllers={controllers}
      isLoading={isLoading}
      loadingMessage={tableLoading}
      emptyMessage={page('projects', 'empty')}
    />
  );
}

export function ProjectsPage() {
  const { col, page } = useAdminI18n();
  const navigate = useNavigate();
  const location = useLocation();
  const toAreaPath = (path: string): string => withAreaPrefix(path, location.pathname);
  const hasScope = useAuthStore(s => s.hasScope);
  const canCreate = hasScope('project:create');
  const canUpdate = hasScope('project:update');
  const canDelete = hasScope('project:delete');

  const [sorting, setSorting] = useState<SortingState>([{ id: 'createdAt', desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({ pageIndex: 0, pageSize: 25 });
  const [search, setSearch] = useState('');

  const { data: projects = [], isLoading } = useProjects();
  const deleteMutation = useDeleteProject();

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (projects as ProjectRow[]).filter(w => {
      if (!term) return true;
      const name = (w.name ?? '').toLowerCase();
      const tenant = getTenantDisplayName(w).toLowerCase();
      return name.includes(term) || (tenant !== '—' && tenant.includes(term));
    });
  }, [projects, search]);

  const sorted = useMemo(() => {
    if (!sorting.length) return filtered;
    const { id, desc } = sorting[0]!;
    const next = [...filtered];
    next.sort((a, b) => {
      if (id === 'nodeCount') {
        const diff = getNodesCount(a) - getNodesCount(b);
        return desc ? -diff : diff;
      }
      if (id === 'createdAt') {
        const av = new Date(String(a.created_at ?? a.createdAt ?? 0)).getTime();
        const bv = new Date(String(b.created_at ?? b.createdAt ?? 0)).getTime();
        const diff = av - bv;
        return desc ? -diff : diff;
      }
      if (id === 'tenantName') {
        const av = getTenantDisplayName(a);
        const bv = getTenantDisplayName(b);
        const cmp = av.localeCompare(bv);
        return desc ? -cmp : cmp;
      }
      const av = String((a as Record<string, unknown>)[id] ?? '');
      const bv = String((b as Record<string, unknown>)[id] ?? '');
      const cmp = av.localeCompare(bv);
      return desc ? -cmp : cmp;
    });
    return next;
  }, [filtered, sorting]);

  const totalRecords = sorted.length;
  const pageSize = pagination.pageSize;
  const pageCount = Math.max(1, Math.ceil(Math.max(1, totalRecords) / pageSize));
  const safePageIndex = Math.min(pagination.pageIndex, pageCount - 1);

  const pageState: PaginationState = {
    pageIndex: safePageIndex,
    pageSize,
  };

  const paginated = useMemo(() => {
    const start = safePageIndex * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [sorted, safePageIndex, pageSize]);

  const controllers: DynamicTableControllers = {
    sort: {
      state: sorting,
      onSortingChange: updater => {
        setSorting(prev => (typeof updater === 'function' ? updater(prev) : updater));
        setPagination(prev => ({ ...prev, pageIndex: 0 }));
      },
    },
    pagination: {
      state: pageState,
      pageCount,
      rowCount: totalRecords,
      onPaginationChange: updater => {
        setPagination(prev => (typeof updater === 'function' ? updater(prev) : updater));
      },
    },
  };

  return (
    <div className="space-y-2 p-2">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">{page('projects', 'title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{page('projects', 'subtitle')}</p>
        </div>
        {canCreate ? (
          <PagePrimaryActionLink to={toAreaPath('/projects/timeline/new')}>
            {page('projects', 'new')}
          </PagePrimaryActionLink>
        ) : null}
      </div>

      <div className="glass-card overflow-hidden">
        <div className="flex flex-wrap items-end gap-2 border-b border-border p-3">
          <SearchFilter
            id="projects-name"
            label={col('name')}
            value={search}
            onChange={v => {
              setSearch(v);
              setPagination(prev => ({ ...prev, pageIndex: 0 }));
            }}
            placeholder={page('projects', 'search_placeholder')}
            className="[&_input]:h-9 [&_input]:py-1.5 [&_input]:text-sm"
          />
        </div>

        <ProjectsTable
          rows={paginated}
          controllers={controllers}
          rowOffset={pageState.pageIndex * pageState.pageSize}
          canUpdate={canUpdate}
          canDelete={canDelete}
          onView={project => navigate(toAreaPath(`/projects/timeline/${project.id}?view=1`))}
          onEdit={project => navigate(toAreaPath(`/projects/timeline/${project.id}`))}
          onDelete={project => {
            const projectName = project.name ?? String(project.id);
            const shouldDelete = globalThis.confirm(
              page('projects', 'delete_confirm', { name: projectName }),
            );
            if (!shouldDelete) {
              return;
            }
            void deleteMutation
              .mutateAsync(String(project.id))
              .then(() => {
                toast.success(page('projects', 'delete_success', { name: projectName }));
              })
              .catch((error: unknown) => {
                const message =
                  error instanceof Error ? error.message : page('projects', 'delete_failed');
                toast.error(message);
              });
          }}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
